import { Backdrop, Box } from '@mui/material';
import classNames from 'classnames';
import { isString } from 'lodash-es';
import bind from 'memoize-bind';
import moment from 'moment-timezone';
import React, { Component, createRef } from 'react';

import CloseIcon from '@sbu/assets.svg/src/close.svg';
import { TagFields, TagSchema } from '@sbu/hooks.tags';
import { IconButton, Tooltip, Typography } from '@sumologic/yama-ds-components';

import { ClickLink } from 'shared/components/internal/ClickLink';

import css from './Filter.module.scss';
import { FilterChips } from './FilterChips';
import { FilterDropdown, OnSelectValueProps } from './FilterDropdown';
import { FilterInput } from './FilterInput';
import { FilterSentence } from './FilterSentence';
import {
  AllOperators,
  DateFilterModifier,
  defaultSources,
  FieldTypes,
  FilterTypes,
  isNegatableOptionsList,
  ListFieldOption,
  Modifier,
  NegatableListFieldOption,
  Operator,
  PropBasedFilterTypes,
  Source,
  StringOperators,
  SuggestedFilter,
  ValueBasedFilterTypes,
} from './types';
import {
  buildFieldList,
  filterGenericList,
  flattenFieldList,
  getDateFormat,
  getFieldObject,
  getFilteredFieldsList,
  getFilteredModifiersList,
  getFilteredOperatorsList,
  getFilteredOptionsList,
  isKeyValueOperator,
  isRelativeRangeFilter,
  ProcessedField,
} from './utils';

export interface FilterProps {
  activeSource?: string;
  allowMultiple: boolean;
  autofocus: boolean;
  className?: any;
  fields: FieldTypes[];
  filterLabel: string;
  filters: FilterTypes[];
  includeSeconds: boolean;
  placeholderText: string;
  showClearButton: boolean;
  showSources: boolean;
  showTime: boolean;
  sources: Source[];
  suggestedFilters: SuggestedFilter[];
  tagAutocompleteList?: string[];
  tagSchemas?: TagSchema[];
  variant: 'default' | 'compact';
  deserializeKeyValue?: (value: string) => TagFields;
  onFilterChange: (filters: FilterTypes[]) => void;
  onSourceChange: (source: string) => void;
  onClearFilters: () => void;
}

interface FilterState {
  filteredFieldsList: FieldTypes[];
  filteredOperatorsList: Operator[];
  filteredOptionsList: ListFieldOption[] | NegatableListFieldOption[];
  filteredModifiersList: Modifier[];
  filteredSourceList: Source[];
  showDropdown: boolean;
  inputText?: string;
  includeCurrent: boolean;
  activeOptionIndex: number;
  partialFilter: Partial<FilterTypes>;
  partialFilterSelected: boolean;
  partialFilterEditing?: FilterTypes;
  partialModifier: Partial<DateFilterModifier>;
  view?: 'source' | 'fields' | 'operators' | 'value' | 'modifier';
  showTime: boolean;
  dateSelection?: Date;
  processedFields: ProcessedField[];
  flatProcessedFields: ProcessedField[];
  expandedFields: { [key: string]: boolean };
  dateRangeEndSelection?: Date;
  autocompleteList: string[];
  dropdownPosition: { top: number; left: number };
  loading: boolean;
}

export class Filter extends Component<FilterProps, FilterState> {
  static defaultProps: FilterProps = {
    showSources: true,
    suggestedFilters: [],
    sources: [...defaultSources],
    filterLabel: 'Filters',
    fields: [],
    allowMultiple: true,
    autofocus: false,
    showClearButton: true,
    filters: [],
    variant: 'default',
    placeholderText: 'Start typing here to create a filter...',
    showTime: false,
    includeSeconds: false,
    onFilterChange: () => undefined,
    onSourceChange: () => undefined,
    onClearFilters: () => undefined,
  };

  ref = createRef<HTMLDivElement>();
  filterInputRef = createRef<FilterInput>();
  state: FilterState = {
    showDropdown: false,
    expandedFields: {},
    activeOptionIndex: -1,
    filteredFieldsList: [],
    filteredOperatorsList: [],
    filteredOptionsList: [],
    filteredModifiersList: [],
    filteredSourceList: [],
    flatProcessedFields: [],
    autocompleteList: [],
    processedFields: [],
    partialFilter: {},
    partialFilterSelected: false,
    partialModifier: {},
    showTime: this.props.showTime,
    includeCurrent: true,
    dropdownPosition: { top: 0, left: 0 },
    // The loading state is not really applicable unless we're autofocusing.
    // This loading state allows us to wait fully for all the initial data processing
    // to complete before focusing the filters.
    loading: !!this.props.autofocus,
  };

  componentDidMount() {
    if (this.props.autofocus) {
      this.onInputClick();
    }
  }

  // complexity 16
  // eslint-disable-next-line complexity
  componentDidUpdate(prevProps: FilterProps, prevState: FilterState) {
    const { sources, activeSource, filters, autofocus } = this.props;
    const { inputText, view, expandedFields, loading } = this.state;
    const partialFilter = this.state
      .partialFilter as Partial<PropBasedFilterTypes>;

    const filteredFieldsList = this.getActiveFields();

    if (prevState.view !== this.state.view) {
      this.setState({
        filteredFieldsList,
      });
    }

    // TODO: These probably need to call other fns
    if (prevState.inputText !== inputText) {
      if (view === 'source') {
        this.setState({
          filteredSourceList: filterGenericList(sources, inputText),
        });
      } else if (view === 'fields') {
        // If we have typed in text, we should show all expansions
        const expansions = !inputText ? expandedFields : undefined;

        this.setState(this.getFieldMetadata(expansions));
      } else if (view === 'operators') {
        const filteredFieldsList = this.getActiveFields();

        this.setState({
          filteredOperatorsList: getFilteredOperatorsList(
            partialFilter,
            filteredFieldsList,
            inputText
          ),
        });
      } else if (view === 'value') {
        const filteredFieldsList = this.getActiveFields();
        const partialFilterField = getFieldObject(
          partialFilter.prop!,
          filteredFieldsList
        );

        if (partialFilterField && partialFilterField.query) {
          if (inputText && inputText.length > 2) {
            partialFilterField.query(inputText).then((autocompleteList) => {
              this.setState({
                autocompleteList,
              });
            });
          } else {
            this.setState({
              autocompleteList: [],
            });
          }
        }

        this.setState({
          filteredOptionsList: getFilteredOptionsList(
            partialFilter,
            filteredFieldsList,
            inputText
          ),
        });
      } else if (view === 'modifier') {
        this.setState({
          filteredModifiersList: getFilteredModifiersList(inputText),
        });
      }
    }

    // If user changed the source, we should show the dropdown
    if (prevProps.activeSource !== activeSource) {
      this.setViewToFields();
    }

    // If a user clicked/enter key to toggle a field but the input didn't change, lets update the list
    if (
      prevState.expandedFields !== expandedFields &&
      prevState.inputText === inputText
    ) {
      this.setState({
        flatProcessedFields: flattenFieldList(
          this.state.processedFields,
          expandedFields
        ),
      });
    }
    // If autofocusing, initialize the filters on fresh page load
    // (e.g. either going directly to a URL, refreshing, or using navigation.)
    const shouldUpdateState =
      !filters.length &&
      (prevState.loading !== loading ||
        prevProps.autofocus !== autofocus ||
        prevProps.filters.length !== filters.length ||
        prevState.filteredFieldsList.length !== filteredFieldsList.length);

    if (shouldUpdateState) {
      if (
        loading &&
        autofocus &&
        prevState.filteredFieldsList.length !== filteredFieldsList.length
      ) {
        this.setViewToFields();
      }

      this.setState({
        filteredFieldsList,
        loading: false,
      });
    }
  }

  onSelectSource(activeSource: string) {
    this.props.onSourceChange(activeSource);
  }

  onInputClick(isDisabled: boolean = false) {
    if (isDisabled) {
      return false;
    }

    const { activeSource } = this.props;
    const { showDropdown } = this.state;

    if (!showDropdown) {
      if (activeSource) {
        this.setViewToFields();
      } else {
        this.setViewToSources();
      }

      // Small timeout to ensure dom init
      setTimeout(() => this.focusInput(), 100);
    }
  }

  resetView() {
    this.setState({
      showDropdown: false,
      inputText: '',
      view: undefined,
      partialFilterSelected: false,
      partialFilterEditing: undefined,
      expandedFields: {},
      autocompleteList: [],
      partialFilter: {},
      partialModifier: {},
      activeOptionIndex: -1,
      showTime: this.props.showTime,
      includeCurrent: true,
    });
  }

  removeFilter(removedFilter: FilterTypes) {
    const { filters, onFilterChange } = this.props;
    const newFilters = filters.filter((filter) => filter !== removedFilter);
    onFilterChange(newFilters);

    // Trigger window resize for overflow component to update
    window.dispatchEvent(new Event('resize'));
  }

  clearFilters(event: React.MouseEvent) {
    event.stopPropagation();
    this.props.onFilterChange([]);
    this.props.onClearFilters();
    this.resetView();

    // Trigger window resize for overflow component to update
    window.dispatchEvent(new Event('resize'));
  }

  updateDropdownPosition() {
    const inputRef = this.filterInputRef.current?.getRef();
    if (inputRef) {
      const referenceRect = inputRef.getBoundingClientRect();

      this.setState({
        dropdownPosition: {
          top: referenceRect.bottom,
          left: referenceRect.left,
        },
      });
    } else {
      this.setState({
        dropdownPosition: { top: 0, left: 0 },
      });
    }
  }

  focusInput() {
    if (this.filterInputRef.current) {
      this.filterInputRef.current.focusInput();
    }
  }

  onActiveIndexChange(activeOptionIndex: number) {
    this.setState({
      activeOptionIndex,
    });
  }

  getActiveFields(filterText = false) {
    const { fields, activeSource } = this.props;
    const { inputText } = this.state;

    return getFilteredFieldsList(
      fields,
      activeSource!,
      filterText ? inputText?.trim() : undefined
    );
  }

  getFieldMetadata(expansions?: { [key: string]: boolean }) {
    // Filter the list down to only fields that are in this source
    // and match the input text
    const filteredFieldsList = this.getActiveFields(true);

    // Recursively process the fields to create nested groups
    const processedFields = filteredFieldsList.reduce(
      (acc: ProcessedField[], cur: FieldTypes) => buildFieldList(acc, cur),
      []
    );

    // Flatten this list and get only the expanded fields
    const flatProcessedFields = flattenFieldList(processedFields, expansions);

    return {
      filteredFieldsList,
      processedFields,
      flatProcessedFields,
    };
  }

  setViewToSources() {
    const { sources, showTime } = this.props;

    this.setState({
      showDropdown: true,
      inputText: '',
      view: 'source',
      partialFilterSelected: false,
      partialFilterEditing: undefined,
      filteredSourceList: sources,
      expandedFields: {},
      partialFilter: {},
      partialModifier: {},
      activeOptionIndex: -1,
      showTime,
    });

    this.focusInput();
    this.updateDropdownPosition();
  }

  setViewToFields() {
    this.setState({
      partialFilter: {},
      partialModifier: {},
      inputText: '',
      activeOptionIndex: -1,
      showDropdown: true,
      partialFilterSelected: false,
      view: 'fields',
      expandedFields: {},
      ...this.getFieldMetadata({}),
    });

    this.focusInput();
    this.updateDropdownPosition();
  }

  setViewToOperators(
    partialFilter: Partial<PropBasedFilterTypes> = this.state
      .partialFilter as Partial<PropBasedFilterTypes>
  ) {
    const { inputText } = this.state;
    const filteredFieldsList = this.getActiveFields();

    const filteredOperatorsList = getFilteredOperatorsList(
      partialFilter,
      filteredFieldsList,
      inputText
    );

    this.setState({
      view: 'operators',
      showDropdown: true,
      partialFilterSelected: false,
      filteredOperatorsList,
      activeOptionIndex: -1,
      inputText: '',
    });

    this.focusInput();
    this.updateDropdownPosition();
  }

  setViewToValue(
    partialFilter: Partial<ValueBasedFilterTypes> = this.state
      .partialFilter as Partial<ValueBasedFilterTypes>
  ) {
    const { inputText } = this.state;
    const filteredFieldsList = this.getActiveFields();

    this.setState({
      view: 'value',
      inputText: '',
      partialFilterSelected: false,
      activeOptionIndex: -1,
      filteredOptionsList: getFilteredOptionsList(
        partialFilter,
        filteredFieldsList,
        inputText
      ),
    });

    this.focusInput();
    this.updateDropdownPosition();
  }

  setViewToModifier() {
    const { inputText } = this.state;
    const filteredModifiersList = getFilteredModifiersList(inputText);

    this.setState({
      view: 'modifier',
      showDropdown: true,
      partialFilterSelected: false,
      filteredModifiersList,
      activeOptionIndex: -1,
      inputText: '',
    });

    this.focusInput();
    this.updateDropdownPosition();
  }

  addFilter(newFilter: FilterTypes | FilterTypes[], clearFilters = false) {
    const { filters, onFilterChange } = this.props;
    const { partialFilterEditing } = this.state;
    const newFilters = clearFilters ? [] : [...filters];

    const filterAdditions = !Array.isArray(newFilter) ? [newFilter] : newFilter;
    for (const filter of filterAdditions) {
      const valueFilter = filter as ValueBasedFilterTypes;

      // Check for dupes that would be exclusive
      const filterIdx = this.checkForFieldMatch(filter, newFilters);
      if (filterIdx > -1) {
        newFilters.splice(filterIdx, 1);
      }

      // Only allow new filters, if we have exact match just ignore it
      const hasExactMatch = newFilters.find(
        (f: any) => f.prop === valueFilter.prop && f.value === valueFilter.value
      );

      // If editing a active filter, lets replace that filter with this new one
      if (partialFilterEditing) {
        const idx = newFilters.indexOf(partialFilterEditing);
        if (idx > -1) {
          newFilters.splice(idx, 1);
        }
      }

      if (!hasExactMatch || partialFilterEditing) {
        newFilters.push(filter);
      }
    }

    // Trigger window resize for overflow component to update
    window.dispatchEvent(new Event('resize'));

    onFilterChange(newFilters);
  }

  checkForFieldMatch(filter: FilterTypes, newFilters: FilterTypes[]) {
    const fields = this.getActiveFields();

    // Only allow one filter for keyword & certain operators
    const valueFilter = filter as ValueBasedFilterTypes;
    const filterProp = filter ? valueFilter.prop : '';
    const fieldObject = filterProp
      ? getFieldObject(filterProp, fields)
      : undefined;
    let filterIdx = -1;

    // Filter out exclusive matches that result in 0 from our new filter
    if (fieldObject) {
      if (fieldObject.type === 'date' && isRelativeRangeFilter(filter)) {
        // If new filter is date and we have another date already
        // on the same field, lets remove the old one
        filterIdx = newFilters.findIndex((f: any) =>
          ['past', 'next'].includes(f.operator)
        );
      } else if (
        fieldObject.type === 'numeric' ||
        fieldObject.type === 'date'
      ) {
        filterIdx = newFilters.findIndex((f: any) => {
          const has = [
            'equals',
            'greaterThan',
            'greaterThanOrEqual',
            'lessThan',
            'lessThanOrEqual',
            'between',
          ].includes(f.operator);

          if (has) {
            const arrField = getFieldObject(f.prop, fields);

            return (
              arrField !== undefined &&
              arrField.type === fieldObject.type &&
              arrField.prop === filterProp &&
              f.operator === filter.operator
            );
          }
          return false;
        });
      } else if (fieldObject.type === 'list') {
        // Determine if there is an equals on the same prop
        // that would create a impossible match
        filterIdx = newFilters.findIndex((f: any) => {
          if (f.operator === 'equals') {
            const arrField = getFieldObject(f.prop, fields);

            return (
              arrField !== undefined &&
              arrField.type === 'list' &&
              arrField.prop === filterProp
            );
          }

          return false;
        });
      }
    }

    return filterIdx;
  }

  setField(fieldProp: string) {
    const partialFilter = { prop: fieldProp };
    this.setState({
      partialFilter,
    });

    // Have to manually pass partial filter due to setState being async...
    this.setViewToOperators(partialFilter);
  }

  setOperator(operator: AllOperators) {
    if (operator === 'equalsUndefined') {
      this.addFilter({
        ...this.state.partialFilter,
        operator: 'equals',
        value: '',
      } as any);

      this.resetView();
    } else if (operator === 'equalsNull' || operator === 'equalsNotNull') {
      this.addFilter({
        ...this.state.partialFilter,
        operator: 'equals',
        value: null,
        negated: operator === 'equalsNotNull',
      } as any);

      this.resetView();
    } else {
      const partialFilter = {
        ...this.state.partialFilter,
        operator,
      } as Partial<FilterTypes>;

      this.setState({
        partialFilter,
      });

      this.setViewToValue();
    }
  }

  toggleIncludeCurrent() {
    this.setState(({ includeCurrent }) => ({
      includeCurrent: !includeCurrent,
    }));
  }

  setModifier(unit: Modifier) {
    const { partialFilter, partialModifier, includeCurrent } = this.state;
    const filter = partialFilter as PropBasedFilterTypes;
    const modifiers = [{ ...partialModifier, unit: unit.type }];

    if (filter.operator === 'past') {
      const newFilter = {
        ...this.state.partialFilter,
        start: {
          value: 'NOW',
          modifiers,
          round: includeCurrent ? '' : unit.type,
        },
        end: {
          value: 'NOW',
          round: includeCurrent ? '' : unit.type,
        },
      } as any;
      this.addFilter(newFilter);
    } else if (filter.operator === 'next') {
      const newFilter = {
        ...this.state.partialFilter,
        start: {
          value: 'NOW',
          round: includeCurrent ? '' : unit.type,
        },
        end: {
          value: 'NOW',
          modifiers,
          round: includeCurrent ? '' : unit.type,
        },
      } as any;

      this.addFilter(newFilter);
    }

    this.resetView();
  }

  // complexity 12
  // eslint-disable-next-line complexity
  setValue({ value, endValue, index }: OnSelectValueProps) {
    const fields = this.getActiveFields();

    // Trim string to prevent errors on parser
    if (isString(value)) {
      value = value.trim();
    }

    // Dates & Numbers have a special value object
    const partialFilter = this.state.partialFilter as PropBasedFilterTypes;
    if (partialFilter && partialFilter.prop) {
      const fieldObject = getFieldObject(partialFilter.prop, fields);
      if (fieldObject) {
        if (
          fieldObject.type === 'date' &&
          !['equals', 'doesNotEqual', 'past', 'next', 'between'].includes(
            partialFilter.operator
          )
        ) {
          value = { value };
        } else if (fieldObject.type === 'numeric') {
          // Remove any "," in the numeric value before parse
          value = Number(value.replace(/,/g, ''));
        }
      }
    }

    if (['past', 'next'].includes(partialFilter.operator)) {
      this.setModifierValue(Number(value));
    } else if (partialFilter.operator === 'between' && endValue) {
      this.addFilter({
        ...partialFilter,
        start: {
          value,
        },
        end: {
          value: endValue,
        },
      } as any);

      this.resetView();
    } else if (partialFilter.operator) {
      // Check for wildcard search
      if (
        isKeyValueOperator(partialFilter.operator) &&
        !!value &&
        typeof value === 'string' &&
        value.endsWith('*')
      ) {
        this.addFilter({
          ...partialFilter,
          // The way filtering is structured, we don't have a "nice" way
          // to handle wildcard in the value part of a keyValue operator type,
          // so we need to change the operator type manually
          operator: (partialFilter.operator === 'containKeyValue'
            ? 'matchesWildcard'
            : 'doesNotMatchWildcard') as StringOperators,
          value,
        });
      } else {
        const field = fields?.find((f) => f.prop === partialFilter.prop);
        // If we're provided an index and all options provide a negated field,
        // we need to determine if it's negated based on the field's option.
        // This occurs when we need to negate based on the option list values,
        // rather than the operators.
        // Otherwise, we can resort to the original logic.
        if (
          typeof index === 'number' &&
          isNegatableOptionsList(field?.options)
        ) {
          this.addFilter({
            ...partialFilter,
            operator: 'equals',
            value,
            negated: field?.options[index]?.negated ?? false,
          } as any);
        } else {
          this.addFilter({
            ...partialFilter,
            value,
          } as any);
        }
      }

      this.resetView();
    } else {
      const filter = {
        value,
        operator: 'keyword',
        negated: false,
      };

      if (value.startsWith('not:')) {
        filter.value = value.substring(4, value.length + 1);
        filter.negated = true;
      }

      this.addFilter(filter as any);
      this.resetView();
    }
  }

  setModifierValue(value: number) {
    const { partialFilter, partialModifier } = this.state;
    const filter = partialFilter as PropBasedFilterTypes;
    const setNegative = filter.operator === 'past';

    this.setState({
      partialModifier: {
        ...partialModifier,
        value: setNegative ? value * -1 : value,
      },
    });

    this.setViewToModifier();
  }

  selectPartialValue() {
    this.setState({ partialFilterSelected: true });
  }

  editFilterField(filter: FilterTypes) {
    this.removeFilter(filter);
    this.setViewToFields();
  }

  editFilterOperator(filter: FilterTypes) {
    const partialFilter = {
      // @ts-ignore
      prop: filter.prop,
    };

    this.setState({
      partialFilter,
      partialFilterEditing: filter,
      showDropdown: true,
    });

    this.setViewToOperators(partialFilter);
  }

  editFilterValue(filter: FilterTypes) {
    this.setState({
      partialFilter: filter,
      partialFilterEditing: filter,
      showDropdown: true,
    });

    this.setViewToValue(filter as ValueBasedFilterTypes);
  }

  onSuggestionClick(suggestion: SuggestedFilter) {
    this.addFilter(suggestion.filter, suggestion.replace);
    this.resetView();
  }

  onToggleTime(showTime: boolean) {
    this.setState({ showTime });
  }

  onToggleFieldVisibility(prop: string) {
    const { expandedFields } = this.state;

    this.setState({
      expandedFields: {
        ...expandedFields,
        [prop]: !expandedFields[prop],
      },
    });
  }

  onInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { view } = this.state;

    this.setState({
      inputText: event.target.value,
      partialFilterSelected: false,
      showDropdown: true,
      activeOptionIndex: -1,
      view: !view ? 'fields' : view,
    });
  }

  onDropdownDeactivate(event?: React.MouseEvent<HTMLElement>) {
    // Check if the click was on the backdrop itself
    if (event?.target === event?.currentTarget) {
      this.resetView();
    }
  }

  onDateSelection(dateSelection: Date, dateRangeEndSelection?: Date) {
    const momentVal = moment(dateSelection);
    const format = getDateFormat(
      this.state.showTime,
      this.props.includeSeconds
    );
    const inputText = dateRangeEndSelection
      ? momentVal.format(format) +
        ' - ' +
        moment(dateRangeEndSelection).format(format)
      : momentVal.format(format);

    this.setState({
      inputText,
      dateSelection,
      dateRangeEndSelection,
    });
  }

  onSourceClick() {
    this.setViewToSources();
  }

  renderDropdown = () => {
    const {
      activeSource,
      includeSeconds,
      showSources,
      sources,
      suggestedFilters,
      variant,
    } = this.props;
    const {
      activeOptionIndex,
      view,
      inputText,
      filteredFieldsList,
      filteredOperatorsList,
      filteredOptionsList,
      filteredModifiersList,
      showTime,
      partialFilter,
      dateSelection,
      dateRangeEndSelection,
      expandedFields,
      processedFields,
      flatProcessedFields,
      filteredSourceList,
      autocompleteList,
      includeCurrent,
      dropdownPosition,
      loading,
    } = this.state;
    const activeFields = this.getActiveFields();

    return (
      <FilterDropdown
        activeOptionIndex={activeOptionIndex}
        activeSource={activeSource!}
        allFields={activeFields}
        allSources={sources}
        autocompleteList={autocompleteList}
        dateRangeEndSelection={dateRangeEndSelection}
        dateSelection={dateSelection}
        expandedFields={expandedFields}
        fields={filteredFieldsList}
        flatProcessedFields={flatProcessedFields}
        includeCurrent={includeCurrent}
        includeSeconds={includeSeconds}
        inputText={inputText}
        onDateSelection={bind(this.onDateSelection, this)}
        onSelectField={bind(this.setField, this)}
        onSelectModifier={bind(this.setModifier, this)}
        onSelectOperator={bind(this.setOperator, this)}
        onSelectSource={bind(this.onSelectSource, this)}
        onSelectValue={bind(this.setValue, this)}
        onSuggestionClick={bind(this.onSuggestionClick, this)}
        onToggleFieldVisibility={bind(this.onToggleFieldVisibility, this)}
        onToggleIncludeCurrent={bind(this.toggleIncludeCurrent, this)}
        onToggleTime={bind(this.onToggleTime, this)}
        operators={filteredOperatorsList}
        options={filteredOptionsList}
        partialFilter={partialFilter}
        processedFields={processedFields}
        showSources={showSources}
        showTime={showTime}
        sources={filteredSourceList}
        suggestedFilters={suggestedFilters}
        units={filteredModifiersList}
        variant={variant}
        view={view}
        style={{
          position: 'absolute',
          ...dropdownPosition,
        }}
        loading={loading}
      />
    );
  };

  renderSource() {
    const { activeSource, sources } = this.props;
    const source = sources.find((s) => s.prop === activeSource);

    if (!source) {
      return null;
    }

    return (
      <div className={css.sourceContainer}>
        <div className={css.sourceText}>
          In{' '}
          <ClickLink onClick={bind(this.onSourceClick, this)}>
            {source.label}
          </ClickLink>
        </div>
      </div>
    );
  }

  // complexity 20
  // eslint-disable-next-line complexity
  render() {
    const {
      activeSource,
      allowMultiple,
      className,
      deserializeKeyValue,
      fields,
      filterLabel,
      filters,
      includeSeconds,
      placeholderText,
      showClearButton,
      showSources,
      sources,
      suggestedFilters,
      tagAutocompleteList,
      tagSchemas,
      variant,
    } = this.props;
    const {
      view,
      showDropdown,
      inputText,
      partialFilter,
      partialFilterSelected,
      partialModifier,
      filteredFieldsList,
      filteredSourceList,
      activeOptionIndex,
      filteredOperatorsList,
      filteredOptionsList,
      filteredModifiersList,
      showTime,
      flatProcessedFields,
      autocompleteList,
    } = this.state;
    const shouldShowSources = showSources && sources && sources.length;
    const shouldShowClear =
      (filters.length > 0 || shouldShowSources) && showClearButton;
    const isCompact = variant === 'compact';
    const activeFields = this.getActiveFields();
    const isDisabled = !allowMultiple && filters.length === 1 && !view;

    return (
      <div
        className={classNames(css.filter, className, 'fs-block', {
          [css.compactStyle]: isCompact,
          [css.defaultStyle]: variant === 'default',
        })}
        ref={this.ref}
      >
        <Box
          className={css.filterInputContainer}
          onClick={() => this.onInputClick(isDisabled)}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer - 1,
          }}
        >
          {filterLabel && (
            <Typography variant='caption' className={css.fieldLabel}>
              {filterLabel}
            </Typography>
          )}
          {activeSource &&
            showSources &&
            view !== 'source' &&
            this.renderSource()}
          {!allowMultiple && filters.length === 1 && !view && (
            <div className={css.sentenceContainer}>
              <FilterSentence
                fields={activeFields}
                filter={filters[0]}
                onEditField={bind(this.editFilterField, this)}
                onEditOperator={bind(this.editFilterOperator, this)}
                onEditValue={bind(this.editFilterValue, this)}
              />
            </div>
          )}
          <FilterInput
            activeOptionIndex={activeOptionIndex}
            activeSource={activeSource!}
            allowMultiple={allowMultiple}
            allSources={sources}
            autocompleteList={autocompleteList}
            disabled={isDisabled}
            fields={fields}
            filteredFieldsList={filteredFieldsList}
            filteredModifiersList={filteredModifiersList}
            filteredOperatorsList={filteredOperatorsList}
            filteredOptions={filteredOptionsList}
            filters={filters}
            flatProcessedFields={flatProcessedFields}
            includeSeconds={includeSeconds}
            onActiveIndexChange={bind(this.onActiveIndexChange, this)}
            onClose={bind(this.resetView, this)}
            onDateSelection={bind(this.onDateSelection, this)}
            onInputChange={bind(this.onInputChange, this)}
            onSelectField={bind(this.setField, this)}
            onSelectModifier={bind(this.setModifier, this)}
            onSelectOperator={bind(this.setOperator, this)}
            onSelectPartial={bind(this.selectPartialValue, this)}
            onSelectSource={bind(this.onSelectSource, this)}
            onSelectValue={bind(this.setValue, this)}
            onSetFieldsView={bind(this.setViewToFields, this)}
            onSetOperatorsView={bind(this.setViewToOperators, this)}
            onSetValueView={bind(this.setViewToValue, this)}
            onToggleFieldVisibility={bind(this.onToggleFieldVisibility, this)}
            partialFilter={partialFilter}
            partialFilterSelected={partialFilterSelected}
            partialModifier={partialModifier}
            placeholderText={placeholderText}
            ref={this.filterInputRef}
            showTime={showTime}
            sources={filteredSourceList}
            suggestedFilters={suggestedFilters}
            tagAutocompleteList={tagAutocompleteList}
            tagSchemas={tagSchemas}
            value={inputText}
            variant={variant}
            view={view}
          />
          {allowMultiple && (
            <FilterChips
              deserializeKeyValue={deserializeKeyValue}
              fields={activeFields}
              filters={filters}
              onEditField={bind(this.setViewToFields, this)}
              onEditOperator={bind(this.editFilterOperator, this)}
              onEditValue={bind(this.editFilterValue, this)}
              removeFilter={bind(this.removeFilter, this)}
              clearFilters={bind(this.clearFilters, this)}
            />
          )}
          {shouldShowClear && (
            <div className={css.clearContainer}>
              <Tooltip title='Clear all filters' describeChild>
                <IconButton
                  size='medium'
                  aria-label='Clear all filters'
                  className={css.clearFilters}
                  data-sumo-test='filter-clear-btn'
                  onClick={bind(this.clearFilters, this)}
                  sx={{ p: 0 }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </Box>
        <Backdrop
          data-sumo-test='filter-backdrop'
          open={showDropdown}
          invisible
          onClick={bind(this.onDropdownDeactivate, this)}
          sx={{ opacity: '1 !important' }}
        >
          {(view !== 'value' ||
            !isKeyValueOperator(partialFilter?.operator as string)) &&
            this.renderDropdown()}
        </Backdrop>
      </div>
    );
  }
}
